import { blogPosts, type BlogPost } from "@/data/blogPosts";

// MAPA DE NEGOCIOS: cada key es un valor que puede aparecer en targetAudience
// El value es el nombre normalizado que se mostrará
const BUSINESS_TYPE_MAP: Record<string, string> = {
  // Consultoras (agrupa variantes)
  "Consultores": "Consultoras",
  "Consultorías": "Consultoras", 
  "Consultoras": "Consultoras",
  
  // Despachos (agrupa variantes)
  "Abogados": "Despachos",
  "Despachos": "Despachos",
  "Despachos de abogados": "Despachos",

  // Ecommerce (agrupa variantes)
  "Ecommerce": "Ecommerce",
  "Tiendas online": "Ecommerce",
  "Amazon sellers": "Ecommerce",
  "Marketplaces": "Ecommerce",
  
  // Tipos directos (sin variantes)
  "Restaurantes": "Restaurantes",
  "Inmobiliarias": "Inmobiliarias",
  "Franquicias": "Franquicias",
  "Tiendas": "Tiendas",
  "Comercios": "Tiendas",
  "Gestorías": "Gestorías",
  "Asesorías": "Asesorías",
  "Agencias": "Agencias",
  "Clínicas": "Clínicas",
  "Hoteles": "Hoteles",
  "Gimnasios": "Gimnasios",
  "Academias": "Academias",
};

// MAPA DE ACTIVIDADES: cada key es un valor que puede aparecer en departments
// El value es el nombre normalizado que se mostrará
// Estandarizado a formato largo
const ACTIVITY_TYPE_MAP: Record<string, string> = {
  // Formatos largos (estándar)
  "Marketing y Comunicación": "Marketing y Comunicación",
  "Ventas y Comercial": "Ventas y Comercial",
  "Dirección General": "Dirección General",
  "Operaciones y Productividad": "Operaciones y Productividad",
  "Finanzas y Administración": "Finanzas y Administración",
  "RRHH y Formación": "RRHH y Formación",
  "Tecnología e Innovación": "Tecnología e Innovación",
  "Atención al Cliente": "Atención al Cliente",
  "Legal": "Legal",
  
  // Formatos cortos (mapean a formato largo)
  "Marketing": "Marketing y Comunicación",
  "Comunicación": "Marketing y Comunicación",
  "Redes Sociales": "Marketing y Comunicación",
  "Diseño": "Marketing y Comunicación",
  "Ventas": "Ventas y Comercial",
  "Comercial": "Ventas y Comercial",
  "Dirección": "Dirección General",
  "Gestión": "Dirección General",
  "Operaciones": "Operaciones y Productividad",
  "Administración": "Finanzas y Administración",
  "Finanzas": "Finanzas y Administración",
  "RRHH": "RRHH y Formación",
  "Formación": "RRHH y Formación",
  "Desarrollo": "Tecnología e Innovación",
  
  // Roles de targetAudience que son actividades
  "Comerciales": "Ventas y Comercial",
  "Directores": "Dirección General",
  "Formadores": "RRHH y Formación",
  "Marketing B2B": "Marketing y Comunicación",
  "Diseño Web": "Tecnología e Innovación",
};

// Valores a IGNORAR completamente (no son ni negocios ni actividades)
const IGNORED_VALUES = new Set([
  "Autónomos", "PYMEs", "Emprendedores", "Pequeñas empresas",
  "Freelancers", "Equipos remotos", "Servicios profesionales",
  "Moda", "Decoración", "Retail", "Creadores", "Content creators",
  "Creadores de contenido"
]);

export function classifyPost(post: BlogPost) {
  const businessTypes = new Set<string>();
  const activityTypes = new Set<string>();
  
  // 1. Procesar targetAudience (fuente de negocios Y algunos roles)
  post.targetAudience?.forEach(value => {
    if (IGNORED_VALUES.has(value)) return;
    
    if (BUSINESS_TYPE_MAP[value]) {
      businessTypes.add(BUSINESS_TYPE_MAP[value]);
    } else if (ACTIVITY_TYPE_MAP[value]) {
      activityTypes.add(ACTIVITY_TYPE_MAP[value]);
    }
    // Si no está en ningún mapa, se ignora (no se detectan tipos nuevos)
  });
  
  // 2. Procesar departments (fuente de actividades)
  post.departments?.forEach(value => {
    if (value === "Consultoría") return; // Caso especial: es negocio, no actividad
    
    if (ACTIVITY_TYPE_MAP[value]) {
      activityTypes.add(ACTIVITY_TYPE_MAP[value]);
    }
    // Si no está en el mapa, se ignora
  });
  
  return {
    businessTypes: Array.from(businessTypes),
    activityTypes: Array.from(activityTypes)
  };
}

export function getAllClassifiedTypes() {
  const businessMap = new Map<string, string[]>();
  const activityMap = new Map<string, string[]>();
  
  blogPosts.forEach(post => {
    const { businessTypes, activityTypes } = classifyPost(post);
    
    businessTypes.forEach(type => {
      if (!businessMap.has(type)) businessMap.set(type, []);
      businessMap.get(type)!.push(post.id);
    });
    
    activityTypes.forEach(type => {
      if (!activityMap.has(type)) activityMap.set(type, []);
      activityMap.get(type)!.push(post.id);
    });
  });
  
  return {
    businessEntries: Array.from(businessMap.entries()).sort((a, b) => a[0].localeCompare(b[0])),
    activityEntries: Array.from(activityMap.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  };
}

// Legacy compatibility: función para extraer tipos de negocio de posts relacionados
interface BusinessTypeResult {
  types: string[];
  hasContentCreators: boolean;
}

export function extractBusinessTypes(relatedPostIds: string[]): BusinessTypeResult {
  const allTypes = new Set<string>();
  let hasContentCreators = false;
  
  relatedPostIds.forEach(postId => {
    const post = blogPosts.find(p => p.id === postId);
    if (!post) return;
    
    const { businessTypes } = classifyPost(post);
    businessTypes.forEach(type => allTypes.add(type));
    
    // Detectar creadores de contenido en targetAudience
    if (post.targetAudience?.some(audience => 
      audience.includes("Creadores") || 
      audience.includes("Content creators") ||
      audience.includes("Creadores de contenido")
    )) {
      hasContentCreators = true;
    }
  });
  
  return {
    types: Array.from(allTypes).sort(),
    hasContentCreators
  };
}
